import type { VariantTemplateCreateMutationResponse } from '~/api/graphql/mutations/variantTemplateCreate.generated';
import type { VariantPriceCreateMutationResponse } from '~/api/graphql/mutations/variantPriceCreate.generated';
import type { CreateVariantInput, CreateVariantPriceInput, VariantTemplate } from '~/types/graphql';
import type { Campaign, Variant } from '~/modules/campaign/types';

import { useVariantTemplateCreateMutation } from '~/api/graphql/mutations/variantTemplateCreate.generated';
import { useVariantPriceCreateMutation } from '~/api/graphql/mutations/variantPriceCreate.generated';
import { useVariantCreateMutation } from '~/api/graphql/mutations/variantCreate.generated';
import { VARIANT_PRESET } from '~/constants/campaign';

export type IControlVariantTemplate = Pick<
  CreateVariantInput,
  'templateType' | 'templateSuffix' | 'themePageID'
> & {
  template?: Pick<VariantTemplate, 'themeID' | 'title' | 'thumbnailURL'>;
};

export interface ICreateCampaignVariantOptions {
  controlTemplate?: IControlVariantTemplate;
}

export const useCreateCampaignVariant = (
  props: {
    template?: Omit<VariantTemplate, 'id'>;
    price?: Omit<CreateVariantPriceInput, 'variantID'>;
  } = {},
) => {
  const createVariantMutation = useVariantCreateMutation();
  const createVariantTemplateMutation = useVariantTemplateCreateMutation();
  const createVariantPriceMutation = useVariantPriceCreateMutation();

  const createVariants = async (campaignId: string, controlTemplate?: IControlVariantTemplate) => {
    const { template: _controlVariantTemplate, ...controlVariantInput } = controlTemplate ?? {};

    const [originResult, nonOriginResult] = await Promise.all([
      createVariantMutation.mutateAsync({
        input: {
          ...VARIANT_PRESET,
          ...controlVariantInput,
          campaignID: campaignId,
          isOrigin: true,
        },
      }),
      createVariantMutation.mutateAsync({
        input: {
          ...VARIANT_PRESET,
          campaignID: campaignId,
          isOrigin: false,
        },
      }),
    ]);

    return {
      variantOrigin: originResult?.variantCreate,
      variantNonOrigin: nonOriginResult?.variantCreate,
    };
  };

  const createVariantTemplates = async (
    variantOriginId: string,
    variantNonOriginId: string,
    controlVariantTemplate?: IControlVariantTemplate['template'],
  ) => {
    const [originTemplateResult, nonOriginTemplateResult] = await Promise.all([
      createVariantTemplateMutation.mutateAsync({
        input: {
          ...props.template,
          ...controlVariantTemplate,
          variantID: variantOriginId,
        },
      }),
      createVariantTemplateMutation.mutateAsync({
        input: {
          ...props.template,
          variantID: variantNonOriginId,
        },
      }),
    ]);

    return {
      variantTemplateOrigin: originTemplateResult?.variantTemplateCreate,
      variantTemplateNonOrigin: nonOriginTemplateResult?.variantTemplateCreate,
    };
  };

  const createVariantPrices = async (
    variantOriginId: string,
    variantNonOriginId: string,
    price: Omit<CreateVariantPriceInput, 'variantID'>,
  ) => {
    const [originPriceResult, nonOriginPriceResult] = await Promise.all([
      createVariantPriceMutation.mutateAsync({
        input: {
          ...price,
          variantID: variantOriginId,
        },
      }),
      createVariantPriceMutation.mutateAsync({
        input: {
          ...price,
          variantID: variantNonOriginId,
        },
      }),
    ]);

    return {
      variantPriceOrigin: originPriceResult?.variantPriceCreate,
      variantPriceNonOrigin: nonOriginPriceResult?.variantPriceCreate,
    };
  };

  const formatVariant = (params: {
    variant: Variant;
    templateData?: any;
    priceData?: VariantPriceCreateMutationResponse['variantPriceCreate'];
  }): Variant => {
    const { variant, templateData, priceData } = params;

    return {
      ...variant,
      ...(props.template && {
        template: {
          ...templateData,
          redirectURL: variant.template?.redirectURL ?? '',
          themeID: variant.template?.themeID ?? '',
        },
      }),
      ...(props.price &&
        priceData && {
          price: priceData,
        }),
    };
  };

  const createCampaignVariant = async (
    campaign: Campaign,
    options?: ICreateCampaignVariantOptions,
  ) => {
    const { variantOrigin, variantNonOrigin } = await createVariants(
      campaign.id,
      options?.controlTemplate,
    );

    if (!variantOrigin || !variantNonOrigin) {
      throw new Error('Failed to create variants');
    }

    let dataVariantOrigin: VariantTemplateCreateMutationResponse['variantTemplateCreate'];
    let dataVariantNonOrigin: VariantTemplateCreateMutationResponse['variantTemplateCreate'];
    let priceVariantOrigin: VariantPriceCreateMutationResponse['variantPriceCreate'];
    let priceVariantNonOrigin: VariantPriceCreateMutationResponse['variantPriceCreate'];

    if (props.template) {
      const { variantTemplateOrigin, variantTemplateNonOrigin } = await createVariantTemplates(
        variantOrigin.id,
        variantNonOrigin.id,
        options?.controlTemplate?.template,
      );
      dataVariantOrigin = variantTemplateOrigin;
      dataVariantNonOrigin = variantTemplateNonOrigin;
    }

    if (props.price) {
      const { variantPriceOrigin, variantPriceNonOrigin } = await createVariantPrices(
        variantOrigin.id,
        variantNonOrigin.id,
        props.price,
      );
      priceVariantOrigin = variantPriceOrigin;
      priceVariantNonOrigin = variantPriceNonOrigin;
    }

    const formattedVariantOrigin = formatVariant({
      variant: variantOrigin,
      templateData: dataVariantOrigin,
      priceData: priceVariantOrigin,
    });
    const formattedVariantNonOrigin = formatVariant({
      variant: variantNonOrigin,
      templateData: dataVariantNonOrigin,
      priceData: priceVariantNonOrigin,
    });

    return {
      ...campaign,
      variants: [formattedVariantOrigin, formattedVariantNonOrigin],
    };
  };

  return {
    createCampaignVariant,
  };
};
